import React from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import FastImage from '@d11/react-native-fast-image';
import { Icon } from 'react-native-paper';
import {
  forceHttps,
  formatMoney,
  formatNumberDecimal,
  iconSize,
} from '../../../utils/libs';
import { getProductDisplayName } from '../../../utils/productDisplayName';
import type { BackofficeProduct } from '../../../redux/backoffice/productsSlice';
import {
  getProductStockFlags,
  getSavedVariants,
  getStockNotes,
  getVariantStockTotal,
  isVariantProduct,
  toFiniteNumber,
} from '../../../utils/backofficeProductView';
import {
  appendServiceMeta,
  getProductKind,
  isServiceProduct,
} from '../../../utils/productKind';
import type { InventoryStyles } from './inventoryStyles';

type Props = {
  styles: InventoryStyles;
  theme: any;
  product: BackofficeProduct;
  index: number;
  currencyText: string;
  onPress: (product: BackofficeProduct) => void;
  /** True when the list shows two rows side by side (wide web windows). */
  inGrid?: boolean;
};

/** "Ksh 90", "Ksh 180/kg" for products sold per unit, "Ksh 250 - Ksh 450" across variants. */
export const formatRowPrice = (
  currencyText: string,
  product: BackofficeProduct,
): string => {
  if (isVariantProduct(product)) {
    const prices = getSavedVariants(product)
      .map(variant => toFiniteNumber(variant?.bp_variant_selling_price))
      .filter((price): price is number => price != null);
    if (!prices.length) return '—';
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return `${currencyText} ${formatMoney(min)}`;
    return `${currencyText} ${formatMoney(min)} - ${currencyText} ${formatMoney(max)}`;
  }
  const price = toFiniteNumber(product.bp_product_selling_price);
  if (price == null) return '—';
  const unit =
    product.bp_product_price_type === 'unit'
      ? String(product.bp_product_unit_of_mass || '').trim()
      : '';
  return `${currencyText} ${formatMoney(price)}${unit ? `/${unit}` : ''}`;
};

/** The red line under the name: "Out of stock", "Low stock · min 5", "Low stock: 1L". */
export const getRowNotes = (product: BackofficeProduct): string[] =>
  getStockNotes(product, { showMinimum: true });

const InventoryProductRow: React.FC<Props> = ({
  styles,
  theme,
  product,
  index,
  currencyText,
  onPress,
  inGrid,
}) => {
  const imageUri = String(product?.bp_product_image || '');
  const hasVariants = isVariantProduct(product);
  const isService = isServiceProduct(product);
  const variants = getSavedVariants(product);
  const flags = getProductStockFlags(product);
  const unit =
    !hasVariants && product.bp_product_price_type === 'unit'
      ? String(product.bp_product_unit_of_mass || '').trim()
      : '';
  const quantity = hasVariants
    ? getVariantStockTotal(product)
    : (toFiniteNumber(product.bp_product_quantity) ?? 0);
  const name = hasVariants
    ? String(product.bp_product_name || '').trim()
    : getProductDisplayName(product);
  // "Haircuts · Service": the quiet marker a service carries everywhere
  const category = appendServiceMeta(
    product.bp_product_category,
    getProductKind(product),
  );
  // Non-breaking space: "2 variants" wraps as one piece
  const variantCount = hasVariants
    ? `${variants.length}\u00A0${variants.length === 1 ? 'variant' : 'variants'}`
    : '';
  const notes = getRowNotes(product);
  const danger = flags.status !== 'ok';

  return (
    <Pressable
      onPress={() => onPress(product)}
      accessibilityRole="button"
      style={({ pressed, hovered }: any) => [
        styles.row,
        inGrid && styles.rowInGrid,
        Platform.OS === 'web' && hovered && styles.rowHovered,
        pressed && styles.rowPressed,
      ]}>
      <Text style={[styles.rowIndex, inGrid && styles.rowIndexInGrid]}>
        {index + 1}
      </Text>
      {imageUri ? (
        <FastImage
          source={{
            uri: forceHttps(imageUri),
            priority: FastImage.priority.normal,
            cache: FastImage.cacheControl.immutable,
          }}
          style={styles.thumb as any}
          resizeMode={FastImage.resizeMode.cover}
        />
      ) : (
        <View style={styles.thumbPlaceholder}>
          <Icon
            source={isService ? 'briefcase-outline' : 'image-plus'}
            size={iconSize(18)}
            color={theme.colors.textSecondary}
          />
        </View>
      )}
      <View style={styles.rowInfo}>
        <Text style={styles.rowName} numberOfLines={1}>
          {name}
        </Text>
        {category || variantCount ? (
          <Text style={styles.rowMeta} numberOfLines={2}>
            {category}
            {category && variantCount ? ' · ' : ''}
            {variantCount ? (
              <Text style={styles.rowMetaVariants}>{variantCount}</Text>
            ) : null}
          </Text>
        ) : null}
        {notes.map(note => (
          <Text key={note} style={styles.rowNote} numberOfLines={2}>
            {note}
          </Text>
        ))}
      </View>
      <View style={styles.rowRight}>
        {isService ? (
          // No stock figure: the service keeps none
          <Text style={styles.rowQty}>
            <Text style={styles.rowQtyAffix}>Service</Text>
          </Text>
        ) : (
          <Text style={[styles.rowQty, danger && styles.rowQtyDanger]}>
            <Text style={styles.rowQtyAffix}>In stock: </Text>
            {formatNumberDecimal(quantity)}
            {unit ? (
              <Text style={styles.rowQtyAffix}>{` ${unit}`}</Text>
            ) : null}
          </Text>
        )}
        <Text style={styles.rowPrice} numberOfLines={1}>
          {formatRowPrice(currencyText, product)}
        </Text>
      </View>
    </Pressable>
  );
};

export default React.memo(InventoryProductRow);
